import {useCallback, useState} from "react";
import {parse, stringify} from "flatted";

// inspired by https://usehooks.com/useLocalStorage/
// Added the ability to just store strings using it
export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? parse(item) : initialValue;
    } catch (error) {
      // Plain strings are stored unserialized, so a parse failure means the raw item is the value
      const item = window.localStorage.getItem(key);
      if (item) {
        return item;
      }

      console.log(error);
      return initialValue;
    }
  });

  const setValue = useCallback((value: T | ((val: T) => T)) => {
    setStoredValue(previous => {
      const valueToStore = value instanceof Function ? value(previous) : value;
      try {
        if (typeof window !== "undefined") {
          if (typeof valueToStore === "string") {
            window.localStorage.setItem(key, valueToStore);
          } else {
            window.localStorage.setItem(key, stringify(valueToStore));
          }
        }
      } catch (error) {
        console.log(error);
      }
      return valueToStore;
    });
  }, [key]);

  return [storedValue, setValue] as const;
}
